/**
 * Toast Provider - Reusable notifications
 *  */

import { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import * as ToastPrimitives from '@radix-ui/react-toast';
import { CheckCircle2, X, XCircle, AlertTriangle } from 'lucide-react';

import { LoadingSpinner } from '@/components/ui';
import { cn } from '@/lib/utils';

type ToastType = 'default' | 'success' | 'error' | 'warning' | 'loading';

interface Toast {
  id: string;
  title: string;
  description?: string;
  type: ToastType;
  /** Loading toasts don't auto-dismiss */
  persistent?: boolean;
}

interface ToastContextType {
  toast: (options: Omit<Toast, 'id'>) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  warning: (title: string, description?: string) => void;
  /** Shows a loading toast with spinner. Returns id to pass to dismiss/update. Does NOT auto-dismiss. */
  loading: (title: string, description?: string) => string;
  /** Updates an existing toast (e.g. loading progress). Call with id from loading(). */
  update: (id: string, title: string, description?: string) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const typeConfig: Record<
  ToastType,
  { icon: typeof CheckCircle2 | null; border: string; iconBg: string; iconColor: string }
> = {
  default: {
    icon: null,
    border: 'border-border',
    iconBg: 'bg-muted',
    iconColor: 'text-muted-foreground',
  },
  loading: {
    icon: null,
    border: 'border-primary/60',
    iconBg: 'bg-primary/10',
    iconColor: 'text-primary',
  },
  success: {
    icon: CheckCircle2,
    border: 'border-success/60',
    iconBg: 'bg-success/10',
    iconColor: 'text-success',
  },
  error: {
    icon: XCircle,
    border: 'border-destructive/60',
    iconBg: 'bg-destructive/10',
    iconColor: 'text-destructive',
  },
  warning: {
    icon: AlertTriangle,
    border: 'border-warning/60',
    iconBg: 'bg-warning/10',
    iconColor: 'text-warning',
  },
} as const;

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((options: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts((prev) => [...prev, { ...options, id }]);

    if (!options.persistent) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, 5000);
    }
    return id;
  }, []);

  const toast = useCallback(
    (options: Omit<Toast, 'id'>) => addToast(options),
    [addToast],
  );

  const success = useCallback(
    (title: string, description?: string) =>
      addToast({ title, description, type: 'success' }),
    [addToast],
  );

  const error = useCallback(
    (title: string, description?: string) =>
      addToast({ title, description, type: 'error' }),
    [addToast],
  );

  const warning = useCallback(
    (title: string, description?: string) =>
      addToast({ title, description, type: 'warning' }),
    [addToast],
  );

  const loading = useCallback(
    (title: string, description?: string) =>
      addToast({ title, description, type: 'loading', persistent: true }),
    [addToast],
  );

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const update = useCallback(
    (id: string, title: string, description?: string) => {
      setToasts((prev) =>
        prev.map((t) => (t.id === id ? { ...t, title, description } : t)),
      );
    },
    [],
  );

  const dismiss = useCallback(
    (id: string) => setToasts((prev) => prev.filter((t) => t.id !== id)),
    [],
  );

  return (
    <ToastContext.Provider value={{ toast, success, error, warning, loading, update, dismiss }}>
      <ToastPrimitives.Provider swipeDirection="right">
        {children}

        {toasts.map((t) => {
          const config = typeConfig[t.type] ?? typeConfig.default;
          const Icon = config.icon;

          return (
            <ToastPrimitives.Root
              key={t.id}
              duration={t.persistent ? Number.POSITIVE_INFINITY : 5000}
              className={cn(
                'group pointer-events-auto relative flex w-full max-w-[360px] items-start gap-3 overflow-hidden rounded-lg border bg-card px-4 py-3 pr-9 shadow-lg transition-all',
                'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-top-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
                config.border,
              )}
              onOpenChange={(open) => !open && removeToast(t.id)}
            >
              {t.type === 'loading' ? (
                <div
                  className={cn(
                    'mt-0.5 flex shrink-0 items-center justify-center rounded-full p-1',
                    config.iconBg,
                    config.iconColor,
                  )}
                >
                  <LoadingSpinner size="xs" />
                </div>
              ) : null}
              {Icon ? (
                <div className={cn('mt-0.5 shrink-0 rounded-full p-1', config.iconBg, config.iconColor)}>
                  <Icon className="h-4 w-4" aria-hidden />
                </div>
              ) : null}
              <div className="min-w-0 flex-1 grid gap-0.5">
                <ToastPrimitives.Title className="text-sm font-semibold leading-tight text-foreground">
                  {t.title}
                </ToastPrimitives.Title>
                {t.description && (
                  <ToastPrimitives.Description className="text-xs text-muted-foreground leading-snug">
                    {t.description}
                  </ToastPrimitives.Description>
                )}
              </div>
              <ToastPrimitives.Close className="absolute right-2 top-2 rounded p-1.5 text-muted-foreground opacity-60 transition-opacity hover:opacity-100 focus:opacity-100 focus:outline-none">
                <X className="h-3.5 w-3.5" aria-hidden />
              </ToastPrimitives.Close>
            </ToastPrimitives.Root>
          );
        })}

        <ToastPrimitives.Viewport className="fixed bottom-4 right-4 z-[250] flex flex-col-reverse gap-2 p-0" />
      </ToastPrimitives.Provider>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
